import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import { formatarTamanho } from '@/lib/mascaras';
import type { Anexo } from '@/features/solicitacoes/tipos';
import { Botao } from '@/components/ui/button';

const TIPOS_ACEITOS = 'application/pdf,image/jpeg,image/png';
const TAMANHO_MAX_MB = 10;

interface PainelAnexosProps {
  solicitacaoId: string;
  anexos: Anexo[];
  /** Falso quando a solicitação já foi concluída. */
  podeEnviar: boolean;
}

export function PainelAnexos({ solicitacaoId, anexos, podeEnviar }: PainelAnexosProps) {
  const queryClient = useQueryClient();
  const entradaArquivo = React.useRef<HTMLInputElement>(null);
  const [baixando, setBaixando] = React.useState<string | null>(null);

  const enviar = useMutation({
    mutationFn: async (arquivo: File) => {
      const form = new FormData();
      form.append('arquivo', arquivo);

      // FormData não passa pelo cliente `api`, que serializa em JSON.
      // O navegador precisa definir o boundary do multipart sozinho.
      return api.postForm(`/solicitacoes/${solicitacaoId}/anexos`, form);
    },
    onSuccess: async () => {
      toast.success('Documento anexado.');
      await queryClient.invalidateQueries({ queryKey: ['solicitacao', solicitacaoId] });
      await queryClient.invalidateQueries({ queryKey: ['historico', solicitacaoId] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível anexar.');
    },
    onSettled: () => {
      // Zera o input para que reenviar o mesmo arquivo dispare onChange.
      if (entradaArquivo.current) entradaArquivo.current.value = '';
    },
  });

  const remover = useMutation({
    mutationFn: (anexoId: string) => api.delete(`/anexos/${anexoId}`),
    onSuccess: async () => {
      toast.success('Documento removido.');
      await queryClient.invalidateQueries({ queryKey: ['solicitacao', solicitacaoId] });
      await queryClient.invalidateQueries({ queryKey: ['historico', solicitacaoId] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível remover.');
    },
  });

  const aoEscolher = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    // Checagem local para dar retorno imediato; o servidor revalida.
    if (arquivo.size > TAMANHO_MAX_MB * 1024 * 1024) {
      toast.error(`O arquivo excede o limite de ${TAMANHO_MAX_MB} MB.`);
      evento.target.value = '';
      return;
    }

    enviar.mutate(arquivo);
  };

  const baixar = async (anexo: Anexo) => {
    setBaixando(anexo.id);
    try {
      const blob = await api.getBlob(`/anexos/${anexo.id}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = anexo.nomeOriginal;
      link.click();
      URL.revokeObjectURL(url);
    } catch (erro) {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível baixar.');
    } finally {
      setBaixando(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {anexos.length === 0 ? (
        <p className="text-silenciado-texto flex items-center gap-2 text-sm">
          <Paperclip className="size-4" aria-hidden="true" />
          Nenhum documento anexado.
        </p>
      ) : (
        <ul className="divide-borda divide-y">
          {anexos.map((anexo) => (
            <li key={anexo.id} className="flex items-center gap-3 py-2.5">
              <FileText className="text-silenciado-texto size-4 shrink-0" aria-hidden="true" />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{anexo.nomeOriginal}</p>
                <p className="text-silenciado-texto text-xs">
                  {formatarTamanho(anexo.tamanhoBytes)} · {anexo.enviadoPor.nome} ·{' '}
                  {formatarDataHora(anexo.criadoEm)}
                </p>
              </div>

              <Botao
                variante="fantasma"
                tamanho="sm"
                onClick={() => void baixar(anexo)}
                disabled={baixando === anexo.id}
                aria-label={`Baixar ${anexo.nomeOriginal}`}
              >
                {baixando === anexo.id ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Download aria-hidden="true" />
                )}
              </Botao>

              {podeEnviar && (
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => {
                    if (confirm(`Remover o documento "${anexo.nomeOriginal}"?`)) {
                      remover.mutate(anexo.id);
                    }
                  }}
                  disabled={remover.isPending}
                  aria-label={`Remover ${anexo.nomeOriginal}`}
                >
                  <Trash2 aria-hidden="true" />
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}

      {podeEnviar ? (
        <div>
          <input
            ref={entradaArquivo}
            type="file"
            accept={TIPOS_ACEITOS}
            onChange={aoEscolher}
            className="apenas-leitor-tela"
            id="entrada-anexo"
          />
          <Botao
            variante="contorno"
            tamanho="sm"
            onClick={() => entradaArquivo.current?.click()}
            disabled={enviar.isPending}
          >
            {enviar.isPending ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : (
              <Upload aria-hidden="true" />
            )}
            {enviar.isPending ? 'Enviando…' : 'Anexar documento'}
          </Botao>
          <p className="text-silenciado-texto mt-1.5 text-xs">
            PDF, JPEG ou PNG, até {TAMANHO_MAX_MB} MB.
          </p>
        </div>
      ) : (
        <p className="text-silenciado-texto text-xs">
          Esta solicitação já foi concluída e não aceita novos documentos.
        </p>
      )}
    </div>
  );
}
